import { getUserPreferences, saveUserPreferences } from '$lib/db';
import { auth } from './auth.svelte';
import { type FilterState, filters } from './filters.svelte';
import { type Controls, fleet } from './fleet.svelte';
import { setPreferencesSyncScheduler } from './persistenceCoordinator';
import { type Basemap, isBasemap, type Tab, type Theme, ui } from './ui.svelte';

export type UserCapabilities = string[] | Record<string, boolean | unknown>;
export type UserViews = Record<string, unknown> | string[];

export interface UserPreferencesConfig {
  tab?: Tab;
  theme?: Theme;
  basemap?: Basemap;
  showRoofs?: boolean;
  showCabinets?: boolean;
  heat?: boolean;
  driverHeat?: boolean;
  legendOpen?: boolean;
  orientationSeen?: boolean;
  mapViewport?: {
    center: [number, number];
    zoom: number;
  };
  filters?: Partial<FilterState>;
  fleet?: Partial<Controls>;
  capabilities?: UserCapabilities;
  views?: UserViews;
}

let syncTimer: ReturnType<typeof setTimeout> | null = null;
let applying = false;
let isDirty = false;
let listenersInitialized = false;
let userCapabilities = $state<UserCapabilities>([]);
let userViews = $state<UserViews>({});

export const preferences = {
  get capabilities(): UserCapabilities {
    return userCapabilities;
  },
  set capabilities(val: UserCapabilities) {
    userCapabilities = val;
    queuePreferencesSync();
  },
  get views(): UserViews {
    return userViews;
  },
  set views(val: UserViews) {
    userViews = val;
    queuePreferencesSync();
  },
  get isDirty(): boolean {
    return isDirty;
  },
  hasCapability(cap: string): boolean {
    if (Array.isArray(userCapabilities)) {
      return userCapabilities.includes(cap);
    }
    if (userCapabilities && typeof userCapabilities === 'object') {
      return Boolean((userCapabilities as Record<string, unknown>)[cap]);
    }
    return false;
  },
  toggleCapability(cap: string, forcedState?: boolean): void {
    let list: string[];
    if (Array.isArray(userCapabilities)) {
      list = [...userCapabilities];
    } else if (userCapabilities && typeof userCapabilities === 'object') {
      list = Object.entries(userCapabilities)
        .filter(([_, v]) => Boolean(v))
        .map(([k]) => k);
    } else {
      list = [];
    }
    const shouldEnable = forcedState !== undefined ? forcedState : !list.includes(cap);
    if (shouldEnable && !list.includes(cap)) {
      list.push(cap);
    } else if (!shouldEnable && list.includes(cap)) {
      list = list.filter((c) => c !== cap);
    }
    userCapabilities = list;
    queuePreferencesSync();
  },
  setCapabilities(val: UserCapabilities): void {
    userCapabilities = val;
    queuePreferencesSync();
  },
  setViews(val: UserViews): void {
    userViews = val;
    queuePreferencesSync();
  },
};

export function collectUserConfig(): UserPreferencesConfig {
  return {
    tab: ui.tab,
    theme: ui.theme,
    basemap: ui.basemap,
    showRoofs: ui.showRoofs,
    showCabinets: ui.showCabinets,
    heat: ui.heat,
    driverHeat: ui.driverHeat,
    legendOpen: ui.legendOpen,
    orientationSeen: ui.orientationSeen,
    mapViewport: ui.mapViewport ?? undefined,
    filters: filters.rawFilters,
    fleet: fleet.rawControls,
    capabilities: userCapabilities,
    views: userViews,
  };
}

export function applyUserConfig(cfg: UserPreferencesConfig): void {
  applying = true;
  try {
    if (cfg.tab === 'map' || cfg.tab === 'dashboard' || cfg.tab === 'fleet') {
      ui.tab = cfg.tab;
    }
    if (cfg.theme === 'light' || cfg.theme === 'dark') {
      ui.setTheme(cfg.theme);
    }
    if (isBasemap(cfg.basemap)) {
      ui.basemap = cfg.basemap;
    }
    if (typeof cfg.showRoofs === 'boolean') {
      ui.showRoofs = cfg.showRoofs;
    }
    if (typeof cfg.showCabinets === 'boolean') {
      ui.showCabinets = cfg.showCabinets;
    }
    if (typeof cfg.heat === 'boolean') {
      ui.heat = cfg.heat;
    }
    if (typeof cfg.driverHeat === 'boolean') {
      ui.driverHeat = cfg.driverHeat;
    }
    if (typeof cfg.legendOpen === 'boolean') {
      ui.legendOpen = cfg.legendOpen;
    }
    if (typeof cfg.orientationSeen === 'boolean') {
      ui.orientationSeen = cfg.orientationSeen;
    }
    if (
      cfg.mapViewport &&
      Array.isArray(cfg.mapViewport.center) &&
      cfg.mapViewport.center.length === 2 &&
      typeof cfg.mapViewport.center[0] === 'number' &&
      typeof cfg.mapViewport.center[1] === 'number' &&
      typeof cfg.mapViewport.zoom === 'number'
    ) {
      ui.setViewport([cfg.mapViewport.center[0], cfg.mapViewport.center[1]], cfg.mapViewport.zoom);
    }
    if (cfg.filters && typeof cfg.filters === 'object') {
      filters.applyFilters(cfg.filters);
    }
    if (cfg.fleet && typeof cfg.fleet === 'object') {
      fleet.applyControls(cfg.fleet);
    }
    if (cfg.capabilities !== undefined) {
      userCapabilities = cfg.capabilities;
    }
    if (cfg.views !== undefined) {
      userViews = cfg.views;
    }
  } finally {
    applying = false;
  }
}

/**
 * Load remote user preferences from Supabase if authenticated.
 * If no remote record exists yet, seeds the current local config to DB.
 */
export async function loadUserPreferences(): Promise<boolean> {
  initEventListeners();
  const u = auth.user;
  if (!u) return false;
  try {
    const remote = await getUserPreferences();
    if (remote && typeof remote === 'object') {
      applyUserConfig(remote as UserPreferencesConfig);
      isDirty = false;
      return true;
    }
    if (auth.isSynthetic) {
      isDirty = false;
      return true;
    }
    // First-time login: seed current local preferences to DB
    await saveUserPreferences(u.id, collectUserConfig() as unknown as Record<string, unknown>);
    isDirty = false;
    return true;
  } catch (err) {
    console.error('Failed to load user preferences from DB:', err);
    return false;
  }
}

function isOffline(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.onLine === 'boolean' &&
    navigator.onLine === false
  );
}

/**
 * Execute an immediate save if a sync is pending.
 */
export async function flushPreferencesSync(): Promise<void> {
  if (syncTimer !== null) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  const u = auth.user;
  if (!u || !isDirty || applying || auth.isSynthetic) return;

  if (isOffline()) {
    return;
  }

  try {
    await saveUserPreferences(u.id, collectUserConfig() as unknown as Record<string, unknown>);
    isDirty = false;
  } catch (err) {
    console.error('Failed to flush user preferences to DB:', err);
  }
}

/**
 * Queue a debounced save of current user config to the database.
 * No-op if user is not authenticated or if remote config is currently being applied.
 */
export function queuePreferencesSync(delayMs = 800): void {
  initEventListeners();
  const u = auth.user;
  if (!u || applying || auth.isSynthetic) return;

  isDirty = true;
  if (syncTimer !== null) clearTimeout(syncTimer);

  syncTimer = setTimeout(() => {
    syncTimer = null;
    const currentUser = auth.user;
    if (!currentUser || !isDirty) return;

    if (isOffline()) {
      // Keep isDirty = true until back online
      return;
    }

    saveUserPreferences(currentUser.id, collectUserConfig() as unknown as Record<string, unknown>)
      .then(() => {
        isDirty = false;
      })
      .catch((err) => {
        console.error('Failed to save user preferences to DB:', err);
      });
  }, delayMs);
}

setPreferencesSyncScheduler(queuePreferencesSync);

/** Set up window/document listeners for offline recovery, tab hides, and cross-tab sync. */
function initEventListeners(): void {
  if (listenersInitialized || typeof window === 'undefined') return;
  listenersInitialized = true;

  // 1. Flush changes when tab is hidden / page is closing
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        void flushPreferencesSync();
      }
    });
  }
  window.addEventListener('beforeunload', () => {
    void flushPreferencesSync();
  });

  // 2. Retry sync when browser comes back online
  window.addEventListener('online', () => {
    if (isDirty && auth.user) {
      queuePreferencesSync(0);
    }
  });

  // 3. Cross-tab sync in same browser via storage event
  window.addEventListener('storage', (e) => {
    if (!e.key || applying) return;
    if (e.key === 'ph-theme' && (e.newValue === 'light' || e.newValue === 'dark')) {
      ui.setTheme(e.newValue);
    } else if (
      e.key === 'ph-tab' &&
      (e.newValue === 'map' || e.newValue === 'dashboard' || e.newValue === 'fleet')
    ) {
      ui.tab = e.newValue;
    } else if (e.key === 'ph-basemap' && isBasemap(e.newValue)) {
      ui.basemap = e.newValue;
    } else if (e.key === 'ph-orientation-seen' && e.newValue) {
      ui.orientationSeen = e.newValue === '1';
    }
  });
}
